import React from 'react';
import gql from 'graphql-tag';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Container as BaseContainer,
  Label as BaseLabel,
} from 'semantic-ui-react';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { isAfter, differenceInDays } from 'date-fns';
import { Button } from '../../../components';
import withModal from '../../../lib/withModal';
import type { ModalDialogTrigger } from '../../../type';
import SalonSubscriptionDialog from '../containers/SalonSubscriptionDialog';

const GET_SALON_QUERY = gql`
  query getSalon($salon: ID!) {
    viewer {
      id
      ... on Administrator {
        salon(id: $salon) {
          id
          name
          address
          settings {
            schedule {
              startAt
              endAt
              step
              format
            }
          }
          subscription {
            active
            subId
            customerId
            priceId
            trialEndsAt
          }
        }
      }
    }
  }
`;

const Container = styled(BaseContainer)`
    &.container {
        background: red;
        margin-top: -1rem;
        margin-bottom: 1rem;
        padding: 0.5rem 0;
        background: #ff1f02;
    }
    &.warning {
        background: #ff9900;
    }
`;

const Content = styled(BaseContainer)`
    &.container {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
`;

const Label = styled(BaseLabel)`
    &.label {
        font-weight: bold;
        padding: 0;
        margin-right: 1rem;
        background: none;
        color: #fff;
    }
`;

const SubscriptionButton = withModal(
  ({ open, onOpen, onClose, salon }: ModalDialogTrigger) => (
    <SalonSubscriptionDialog
      salon={salon}
      trigger={
        <Button onClick={onOpen}>
          <FormattedMessage
            id="pages.salon.SalonNotice.action"
            defaultMessage="Choose plan"
          />
        </Button>
      }
      open={open}
      onClose={onClose}
    />
  ),
);

const SubscriptionNotice = ({ intl }) => {
  const salonId = useSelector(({ user }) => user.get('salon'));
  const { loading, data } = useQuery(GET_SALON_QUERY, {
    variables: { salon: salonId },
    fetchPolicy: 'cache-and-network',
  });
  const salon = !loading && data && data.viewer ? data.viewer.salon : null;

  if (!salon || (salon.subscription && salon.subscription.active)) {
    return null;
  }

  let trial = false;
  let days = 0;
  let daysMsg = '';
  if (
    salon.subscription &&
    salon.subscription.trialEndsAt &&
    isAfter(new Date(parseInt(salon.subscription.trialEndsAt, 10)), new Date())
  ) {
    days = differenceInDays(
      new Date(parseInt(salon.subscription.trialEndsAt, 10)),
      new Date(),
    );
    trial = true;
    const daysRule = intl.formatPlural(days);
    // This message is a plural object ({ one, few, other }), not a plain
    // string, so read it from intl.messages directly — formatMessage() rejects
    // non-string messages in react-intl v2.
    const daysMessage = intl.messages['pages.salon.SalonNotice.days'] || {
      one: '1 day',
      other: '{count} days',
    };
    daysMsg = (daysMessage[daysRule] || daysMessage.other).replace(
      '{count}',
      days,
    );
  }

  return (
    <Container fluid className={trial ? 'warning' : ''}>
      <Content>
        <Label>
          {trial ? (
            <FormattedMessage
              id="pages.salon.SalonNotice.trial"
              defaultMessage="Your trial ends in {count}. Choose payment method to proceed."
              values={{
                count: daysMsg,
              }}
            />
          ) : (
            <FormattedMessage
              id="pages.salon.SalonNotice.ended"
              defaultMessage="Your trial has ended. Choose plan to proceed."
            />
          )}
        </Label>
        <SubscriptionButton salon={salon} />
      </Content>
    </Container>
  );
};

export default injectIntl(SubscriptionNotice);
